import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on app start
  useEffect(() => {
    console.log('AuthContext: Checking auth status on startup');
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    console.log('AuthContext: checkAuthStatus called');
    setLoading(true); // Set loading to true when checking

    try {
      const response = await fetch('/api/v1/users/me?include_area=true', {
        credentials: 'include'
      });

      console.log('AuthContext: Auth check response status:', response.status);
      console.log('AuthContext: Auth check response ok:', response.ok);

      if (response.ok) {
        const userData = await response.json();
        console.log('AuthContext: User data received:', userData);

        // The API returns { users: {...} } instead of { user: {...} }
        const userObj = userData.users || userData.user;

        if (userObj) {
          console.log('AuthContext: Setting user and isAuthenticated to true');
          setUser(userObj);
          setIsAuthenticated(true);
          console.log('AuthContext: User authenticated:', userObj.username);
        } else {
          console.log('AuthContext: No user data in response, clearing auth state');
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        console.log('AuthContext: Auth check failed with status:', response.status);
        // Clear any existing session data
        setUser(null);
        setIsAuthenticated(false);

        // If it's a 401, the user is definitely not authenticated
        if (response.status === 401) {
          console.log('AuthContext: 401 Unauthorized - user not logged in');
        }
      }
    } catch (error) {
      console.error('AuthContext: Auth check network error:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      console.log('AuthContext: Auth check complete, setting loading to false');
      setLoading(false);

      // Log final state after a brief delay to ensure state has updated
      setTimeout(() => {
        console.log('AuthContext: Final auth state - isAuthenticated:', isAuthenticated, 'user:', user?.username || 'none');
      }, 100);
    }
  };

  const login = async (credentials) => {
    console.log('AuthContext: Login attempt for:', credentials.email || credentials.username);
    try {
      const response = await fetch('/api/v1/users/sign_in', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ user: credentials })
      });

      console.log('AuthContext: Login response status:', response.status);

      if (response.ok) {
        const userData = await response.json();
        console.log('AuthContext: Login successful:', userData);

        const userObj = userData.users || userData.user;
        if (userObj) {
          setUser(userObj);
          setIsAuthenticated(true);
          return { success: true };
        } else {
          console.log('AuthContext: Login response missing user data');
          return { success: false, error: 'Invalid response from server' };
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.log('AuthContext: Login failed:', errorData);
        return { success: false, error: errorData.error || 'Login failed' };
      }
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const logout = async () => {
    console.log('AuthContext: Logout called');
    try {
      // Use the correct logout endpoint from Rails routes
      const response = await fetch('/api/v1/users/logout', {
        method: 'DELETE',
        credentials: 'include'
      });
      console.log('AuthContext: Logout API response status:', response.status);
    } catch (error) {
      console.error('AuthContext: Logout error:', error);
    } finally {
      // Always clear local state regardless of API call result
      setUser(null);
      setIsAuthenticated(false);
      console.log('AuthContext: Local auth state cleared');

      // Force a page reload to ensure session is completely cleared
      window.location.href = '/';
    }
  };

  const register = async (userData) => {
    console.log('AuthContext: Registration attempt for:', userData.email);
    try {
      const response = await fetch('/api/v1/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ user: userData })
      });

      console.log('AuthContext: Registration response status:', response.status);

      if (response.ok) {
        const newUserData = await response.json();
        console.log('AuthContext: Registration successful:', newUserData);

        const userObj = newUserData.users || newUserData.user;
        if (userObj) {
          setUser(userObj);
          setIsAuthenticated(true);
          return { success: true };
        } else {
          console.log('AuthContext: Registration response missing user data');
          return { success: false, error: 'Invalid response from server' };
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.log('AuthContext: Registration failed:', errorData);
        return { success: false, error: errorData.error || 'Registration failed' };
      }
    } catch (error) {
      console.error('AuthContext: Registration error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  // Debug logging for state changes
  useEffect(() => {
    console.log('AuthContext: State changed - isAuthenticated:', isAuthenticated, 'loading:', loading, 'user:', user?.username || 'none');
  }, [isAuthenticated, loading, user]);

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    register,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
